/**
 * interior_life_visit — THE ITINERARY'S ROOM, FROM THE VISIT (the interiors programme, slice L, 2026-10-01).
 *
 * `interior_life.lifeRoomOf` is pure over a descriptor; this is the one place that says how a VISIT hands it one
 * (`activeInterior()` + the lift module's truth about what the lift serves) — used by the residents' drawing
 * (`EnvironmentModule`) and by the lift bed, so the bed draws exactly what the game would.
 */
import { interiorSpec } from './building_interiors.js';
import { seatView, residentRoomOf, isPackInterior, partitionBoxesFor, type ActiveInterior } from './interior_wiring.js';
import { furnishPack } from './furnish_packs.js';
import { lifeRoomOf, type LifeRoom, type LifeReserve } from './interior_life.js';
import type { LiftService, WalkSolid } from './interior_lift.js';
import { venueAnchors } from './venue_anchor.js';
import { letUnitsOf } from './rental_client.js'; // (slice H) THE LET ROOMS: a tenant's room is never a resident's
import type { InteriorDescriptor } from './interior_descriptor.js';
import type { InteriorSpec } from './building_interiors.js';

/** The room partitions' solid spans as walk-blocking boxes (the rooms revision; none below it): what the lift's
 *  service rule and the gates' walk rasters add to the drawn pieces. */
export function partitionSolids(d: InteriorDescriptor): WalkSolid[] {
  const out: WalkSolid[] = [];
  for (const q of partitionBoxesFor(d)) if (q.collide) out.push({ floor: q.floor, x0: q.x - q.w / 2, x1: q.x + q.w / 2, z0: q.z - q.d / 2, z1: q.z + q.d / 2 });
  return out;
}

/** How far from the staff side, the customer's place and the queue a resident's spot (and its approach point) stays. */
export const VENUE_KEEP_OFF_M = 0.9;
/** How far OUTSIDE a let room's rectangle an anchor's spot and approach point must stay (the tenant's door: a body
 *  standing at the threshold's own approach point is not "in" the room; one on its wall line is). */
export const LET_KEEP_OFF_M = 0.3;

/**
 * THE LET ROOMS OF A BUILDING, as the itinerary's reserve (slice H, 2026-10-02 — handed on by the tenant's door,
 * `impl-rooms.md` CHECKPOINT 2): `rental_client`'s let store (filled from the live `rental_lease` rows every client
 * subscribes) as rectangles the residents' anchors keep out of. Null when nothing is let (the common case: the room
 * is exactly what it was). Merged over a venue's reserve when both apply.
 */
export function letReserve(buildingId: string, base: LifeReserve | null = null): LifeReserve | null {
  const lets = letUnitsOf(buildingId);
  if (lets.length === 0) return base;
  const rects = lets.map((u) => ({ floor: u.floor, x0: u.x0, z0: u.z0, x1: u.x1, z1: u.z1 }));
  return { staffFloors: base?.staffFloors ?? new Set<number>(), pieces: base?.pieces ?? new Set<string>(), points: base?.points ?? [], rects, rectPad: LET_KEEP_OFF_M };
}

/**
 * THE VENUE'S RESERVE (slice E's rule): in a building that is a bound venue (`venueKind` = 'tavern' | 'clinic' — the
 * venue module's binding of the room the player stands in), the staff side of the counter is the post-holder's alone
 * and the customer's place and the queue are the venue's. Cut from the CANONICAL view (every client reserves the same
 * points): the anchors `venue_anchor.venueAnchors` finds on the ground floor, their pieces (the counter, the clinic's
 * first cot), and every employee-only anchor of that floor.
 */
export function venueReserve(kind: string, d: InteriorDescriptor, spec: Pick<InteriorSpec, 'hw' | 'hd' | 'stair'>, shownGround: ReadonlySet<string>): LifeReserve {
  // the building's own frame (x = z = yaw = 0): `lx` / `lz` are what the itinerary works in
  const va = venueAnchors(kind, d, { x: 0, z: 0, yaw: 0, groundY: 0, hw: spec.hw, hd: spec.hd, stair: spec.stair }, shownGround);
  const pieces = new Set<string>(), points: Array<{ floor: number; x: number; z: number; r: number }> = [];
  for (const a of [...va.staff, ...va.customer, ...va.queue]) {
    points.push({ floor: a.floor, x: a.lx, z: a.lz, r: VENUE_KEEP_OFF_M });
    if (a.piece && a.role !== 'queue') pieces.add(`${a.floor}|${a.piece}`);
  }
  return { staffFloors: new Set([0]), pieces, points };
}

/**
 * The room the residents' itinerary walks in the interior a player stands in — or null (the room is not a planned
 * descriptor: the lever, a bespoke / legacy room, the plan not landed; or nothing in it can be walked to).
 * `svc` = what the lift serves in THIS shell (null = the shell was cut without a shaft, or the lift is off).
 * `venueKind` = the venue this building is bound as, when it is one (the staff side, the customer's place and the queue
 * are then never a resident's — `venueReserve`).
 */
export function lifeRoomForVisit(b: { id: string; key: string; w: number; h: number; d: number }, act: ActiveInterior | null, svc: LiftService | null, venueKind: string | null = null): LifeRoom | null {
  if (!act || act.id !== b.id || act.route !== 'descriptor' || !act.descriptor || !act.view) return null;
  const d = act.descriptor;
  // THE CANONICAL VIEW: full detail and the bought pack — the same anchors on every client, whatever its tier draws
  const canon = seatView(d, { pack: isPackInterior(b.id, b.key) ? (act.pack ? furnishPack(act.pack) : null) : undefined, detail: 2 });
  const shown = new Set<string>(), drawn = new Set<string>();
  for (const f of canon.floors) for (const p of f.pieces) if (p.shown) shown.add(`${f.floor}|${p.slot}`);
  for (const f of act.view.floors) for (const p of f.pieces) if (p.shown) drawn.add(`${f.floor}|${p.slot}`);
  const spec = interiorSpec(b.w, b.h, b.d, b.key, { lift: !!svc }); // the shell as the visit cut it (the lift module holds the truth)
  const shownGround = new Set<string>();
  for (const f of canon.floors) if (f.floor === 0) for (const p of f.pieces) if (p.shown) shownGround.add(p.slot);
  const reserve = letReserve(b.id, venueKind ? venueReserve(venueKind, d, spec, shownGround) : null); // (slice H) + the let rooms
  return lifeRoomOf(d, spec, { sockets: residentRoomOf(d, canon).sockets, shown: (f, s) => shown.has(`${f}|${s}`), drawn: (f, s) => drawn.has(`${f}|${s}`), service: svc, reserve });
}
